import BugReportIcon from '@mui/icons-material/BugReport';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import { ErrorResponse, errorResponseSchema, reportIssueResponseSchema, reportIssueStatusResponseSchema } from '@zw-app/shared/api';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { BACKEND_BASE_URL } from '../constants';
import { db } from '../instant';
import { getConsoleLogs } from '../utils/consoleRingBuffer';
import { blobToRawBase64, captureDomScreenshot } from '../utils/domScreenshot';
import {
    canSubmitReportIssue,
    getReportIssueStatusAlert,
    INITIAL_REPORT_ISSUE_VIEW_STATE,
    reduceReportIssueViewState,
} from '../utils/reportIssueViewState';

const APP_VERSION = process.env.REACT_APP_GIT_COMMIT ?? 'unknown';

function getPlatform(): string {
    const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
    return nav.userAgentData?.platform ?? navigator.platform ?? '';
}

function waitAnimationFrames(count: number): Promise<void> {
    return new Promise(resolve => {
        let remaining = count;
        const step = () => {
            remaining -= 1;
            if (remaining <= 0) {
                resolve();
                return;
            }
            requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    });
}

interface ReportIssueDialogProps {
    open: boolean;
    previewUrl: string | null;
    message: string;
    submitting: boolean;
    statusLoading: boolean;
    statusOk: boolean;
    statusError: string | null;
    errorMessage: string | null;
    onMessageChange: (value: string) => void;
    onRetake: () => void;
    onSubmit: () => void;
    onClose: () => void;
}

function ReportIssueDialog(props: ReportIssueDialogProps) {
    const {
        open,
        previewUrl,
        message,
        submitting,
        statusLoading,
        statusOk,
        statusError,
        errorMessage,
        onMessageChange,
        onRetake,
        onSubmit,
        onClose,
    } = props;

    const canSubmit = canSubmitReportIssue({
        message,
        previewUrl,
        statusLoading,
        statusOk,
        submitting,
    });
    const statusAlert = getReportIssueStatusAlert({
        statusLoading,
        statusOk,
        statusError,
    });
    const alertMessage = statusAlert ?? errorMessage;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Report issue</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    label="What went wrong?"
                    margin="normal"
                    minRows={3}
                    multiline
                    onChange={event => onMessageChange(event.target.value)}
                    value={message}
                />
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Screenshot preview
                    </Typography>
                    {previewUrl ? (
                        <Box
                            alt="Screenshot preview"
                            component="img"
                            src={previewUrl}
                            sx={{ maxWidth: '100%', maxHeight: 240, border: '1px solid #e0e0e0', borderRadius: 1 }}
                        />
                    ) : (
                        <Typography color="text.secondary" variant="body2">
                            Screenshot unavailable — you can still submit your report.
                        </Typography>
                    )}
                </Box>
                {alertMessage ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {alertMessage}
                    </Alert>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button disabled={submitting} onClick={onRetake}>
                    Retake
                </Button>
                <Button disabled={submitting} onClick={onClose}>
                    Cancel
                </Button>
                <Button disabled={!canSubmit} onClick={onSubmit} variant="contained">
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ReportIssueButton() {
    const { user } = db.useAuth();
    const [viewState, dispatch] = useReducer(reduceReportIssueViewState, INITIAL_REPORT_ISSUE_VIEW_STATE);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusOk, setStatusOk] = useState(false);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successIssueUrl, setSuccessIssueUrl] = useState<string | null>(null);
    const screenshotBlobRef = useRef<Blob | null>(null);
    const previewUrlRef = useRef<string | null>(null);

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    /** Idle / closed: no probe in flight, no false “unavailable” Alert. */
    const clearStatusState = useCallback(() => {
        setStatusLoading(false);
        setStatusOk(false);
        setStatusError(null);
    }, []);

    /**
     * Mark status as loading before dialogOpen so first paint does not flash
     * the unavailable Alert (statusOk=false + statusLoading=false).
     */
    const beginStatusLoading = useCallback(() => {
        setStatusLoading(true);
        setStatusOk(false);
        setStatusError(null);
    }, []);

    const runCapture = useCallback(async () => {
        const result = await captureDomScreenshot();
        if (result) {
            revokePreviewUrl();
            previewUrlRef.current = result.objectUrl;
            screenshotBlobRef.current = result.blob;
            dispatch({ type: 'CAPTURE_SUCCEEDED', previewUrl: result.objectUrl });
        } else {
            screenshotBlobRef.current = null;
            dispatch({ type: 'CAPTURE_FAILED' });
        }
    }, [revokePreviewUrl]);

    // T-003: when dialog opens, probe GET /report-issue/status with InstantDB token.
    useEffect(() => {
        if (viewState.phase !== 'dialogOpen' || !user?.refresh_token) {
            return;
        }

        let cancelled = false;
        beginStatusLoading();

        (async () => {
            try {
                const response = await fetch(`${BACKEND_BASE_URL}/report-issue/status`, {
                    method: 'GET',
                    headers: { token: user.refresh_token },
                });
                if (cancelled) {
                    return;
                }

                if (!response.ok) {
                    let parsedError: string | null = null;
                    try {
                        const errorData = (await response.json()) as ErrorResponse;
                        parsedError = errorResponseSchema.validateSync(errorData).error;
                    } catch {
                        parsedError = null;
                    }
                    setStatusOk(false);
                    setStatusError(parsedError);
                    return;
                }

                const resultData = await response.json();
                reportIssueStatusResponseSchema.validateSync(resultData);
                setStatusOk(true);
                setStatusError(null);
            } catch {
                if (!cancelled) {
                    setStatusOk(false);
                    setStatusError(null);
                }
            } finally {
                if (!cancelled) {
                    setStatusLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [beginStatusLoading, viewState.phase, user?.refresh_token]);

    const handleOpen = useCallback(async () => {
        if (!user) {
            return;
        }
        setMessage('');
        setErrorMessage(null);
        // Loading before capture completes so dialogOpen first paint has no false Alert.
        beginStatusLoading();
        dispatch({ type: 'START_CAPTURE' });
        await runCapture();
    }, [beginStatusLoading, runCapture, user]);

    const handleRetake = useCallback(async () => {
        beginStatusLoading();
        dispatch({ type: 'RETAKE' });
        revokePreviewUrl();
        screenshotBlobRef.current = null;
        await waitAnimationFrames(2);
        await runCapture();
    }, [beginStatusLoading, revokePreviewUrl, runCapture]);

    const handleClose = useCallback(() => {
        revokePreviewUrl();
        screenshotBlobRef.current = null;
        setMessage('');
        setErrorMessage(null);
        clearStatusState();
        dispatch({ type: 'CLOSE' });
    }, [clearStatusState, revokePreviewUrl]);

    const handleSubmit = useCallback(async () => {
        if (!user?.refresh_token || !user.email) {
            setErrorMessage('You must be signed in to report an issue.');
            return;
        }
        if (
            !canSubmitReportIssue({
                message,
                previewUrl: viewState.previewUrl,
                statusLoading,
                statusOk,
                submitting,
            })
        ) {
            return;
        }

        setSubmitting(true);
        setErrorMessage(null);

        try {
            let screenshotPngBase64: string | null = null;
            if (screenshotBlobRef.current) {
                screenshotPngBase64 = await blobToRawBase64(screenshotBlobRef.current);
            }

            const body = {
                message: message.trim(),
                screenshotPngBase64,
                consoleLogs: getConsoleLogs(),
                userAgent: navigator.userAgent,
                platform: getPlatform(),
                viewport: { width: window.innerWidth, height: window.innerHeight },
                url: window.location.href,
                appVersion: APP_VERSION,
                userEmail: user.email,
            };

            const response = await fetch(`${BACKEND_BASE_URL}/report-issue`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    token: user.refresh_token,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = (await response.json()) as ErrorResponse;
                const parsed = errorResponseSchema.validateSync(errorData);
                throw new Error(parsed.error || 'Failed to report issue');
            }

            const resultData = await response.json();
            const result = reportIssueResponseSchema.validateSync(resultData);
            handleClose();
            setSuccessIssueUrl(result.issueUrl);
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Failed to report issue';
            setErrorMessage(msg);
        } finally {
            setSubmitting(false);
        }
    }, [handleClose, message, statusLoading, statusOk, submitting, user, viewState.previewUrl]);

    if (!user) {
        return null;
    }

    return (
        <>
            <Button aria-label="Report issue" onClick={handleOpen}>
                <BugReportIcon />
            </Button>
            <ReportIssueDialog
                errorMessage={errorMessage}
                message={message}
                onClose={handleClose}
                onMessageChange={setMessage}
                onRetake={handleRetake}
                onSubmit={handleSubmit}
                open={viewState.phase === 'dialogOpen'}
                previewUrl={viewState.previewUrl}
                statusError={statusError}
                statusLoading={statusLoading}
                statusOk={statusOk}
                submitting={submitting}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                autoHideDuration={10_000}
                onClose={() => setSuccessIssueUrl(null)}
                open={successIssueUrl != null}
            >
                <Alert onClose={() => setSuccessIssueUrl(null)} severity="success" sx={{ width: '100%', maxWidth: 560 }}>
                    Issue reported.{' '}
                    {successIssueUrl ? (
                        <Link href={successIssueUrl} rel="noopener noreferrer" target="_blank">
                            View on GitHub
                        </Link>
                    ) : null}
                </Alert>
            </Snackbar>
        </>
    );
}
